import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, Linkedin, Menu, X, HeartPulse, ChevronRight } from "lucide-react";
import { siteInfo } from "../data/mock";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/expertise", label: "Expertise" },
  { to: "/patient-education", label: "Patient Education" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header className="w-full sticky top-0 z-50 bg-white shadow-sm">
      {/* Top red bar */}
      <div className="bg-[#b81d24] text-white text-[13px]">
        <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center justify-between gap-y-2">
          <div className="flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-white"></span>
            <span>{siteInfo.tagline}</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <a href={`tel:${siteInfo.phone}`} className="flex items-center gap-2 hover:text-amber-200 transition-colors">
              <span className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center">
                <Phone className="w-3.5 h-3.5" />
              </span>
              {siteInfo.phone}
            </a>
            <a href={`mailto:${siteInfo.email}`} className="flex items-center gap-2 hover:text-amber-200 transition-colors">
              <span className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center">
                <Mail className="w-3.5 h-3.5" />
              </span>
              {siteInfo.email}
            </a>
            <span className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center">
                <MapPin className="w-3.5 h-3.5" />
              </span>
              {siteInfo.address}
            </span>
            <div className="flex items-center gap-3 pl-2">
              <a href={siteInfo.socials.instagram} aria-label="Instagram" className="hover:text-amber-200"><Instagram className="w-4 h-4" /></a>
              <a href={siteInfo.socials.facebook} aria-label="Facebook" className="hover:text-amber-200"><Facebook className="w-4 h-4" /></a>
              <a href={siteInfo.socials.linkedin} aria-label="LinkedIn" className="hover:text-amber-200"><Linkedin className="w-4 h-4" /></a>
            </div>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#b81d24] text-white flex items-center justify-center shadow">
            <HeartPulse className="w-7 h-7" />
          </div>
          <div className="leading-tight">
            <div className="text-[#b81d24] font-extrabold text-xl tracking-wide">DR MAMTESH</div>
            <div className="text-[#1f6fb8] font-extrabold text-xl -mt-1">GUPTA</div>
            <div className="text-[10px] tracking-[0.25em] text-gray-500 -mt-0.5">CARDIOLOGIST</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) =>
                `relative font-semibold text-[15px] transition-colors ${
                  isActive ? "text-[#b81d24]" : "text-gray-800 hover:text-[#b81d24]"
                }`
              }
            >
              {({ isActive }) => (
                <span className="flex items-center gap-1.5">
                  <span className={`text-amber-500 ${isActive ? "opacity-100" : "opacity-0"}`}>➜</span>
                  {n.label}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            className="hidden md:inline-flex items-center gap-2 bg-[#b81d24] text-white font-semibold px-5 py-3 rounded-full hover:bg-[#9a1820] transition-colors shadow-md"
          >
            Book Appointment
            <span className="w-6 h-6 rounded-full bg-amber-400 text-[#b81d24] flex items-center justify-center">
              <ChevronRight className="w-4 h-4" />
            </span>
          </Link>
          <button
            className="lg:hidden p-2 rounded-md border border-gray-200"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-gray-100 bg-white">
          <div className="px-4 py-3 flex flex-col gap-2">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-2 px-2 rounded-md font-semibold ${isActive ? "text-[#b81d24] bg-red-50" : "text-gray-800"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center gap-2 bg-[#b81d24] text-white font-semibold px-5 py-3 rounded-full"
            >
              Book Appointment
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
